import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Clock, DollarSign, Users, CheckCircle, XCircle, AlertCircle, TrendingUp, Filter, Search, X } from 'lucide-react';
import './AppointmentManagement.css';

const api = (path, opts = {}) => {
  return fetch(`/api/admin${path}`, {
    credentials: 'include',
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(opts.headers || {})
    }
  });
};

const CURRENCY_SYMBOLS = {
  NGN: '₦',
  GHS: '₵',
  KES: 'KSh',
  TZS: 'TSh',
  UGX: 'USh',
  USD: '$'
};

const formatMoney = (amount, currency = 'NGN') => {
  const symbol = CURRENCY_SYMBOLS[currency] || CURRENCY_SYMBOLS.NGN;
  const numAmount = parseFloat(amount);
  if (isNaN(numAmount)) return `${symbol}0.00`;
  return `${symbol}${numAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Toast notification component
const Toast = ({ type, message, onClose }) => (
  <div className={`am-toast am-toast-${type}`}>
    {type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
    <span className="am-toast-message">{message}</span>
    <button className="am-toast-close" onClick={onClose}>
      <X size={16} />
    </button>
  </div>
);

// Confirmation modal component
const ConfirmModal = ({ isOpen, title, message, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="am-modal-overlay" onClick={onCancel}>
      <div className="am-modal" onClick={(e) => e.stopPropagation()}>
        <div className="am-modal-header am-confirm-header">
          <AlertCircle size={24} />
          <h3 className="am-modal-title">{title}</h3>
        </div>
        <div className="am-modal-body">
          <p>{message}</p>
        </div>
        <div className="am-modal-footer">
          <button className="am-modal-btn am-modal-btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button className="am-modal-btn am-modal-btn-confirm" onClick={onConfirm}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

const AppointmentManagement = () => {
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [merchantFilter, setMerchantFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Pagination
  const [pagination, setPagination] = useState({ current: 1, pages: 1, total: 0 });

  // Modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    appointmentId: null,
    newStatus: null
  });

  // Toast notifications
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((type, message) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchAppointments();
  }, [statusFilter, merchantFilter, startDate, endDate, pagination.current]);

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const response = await api('/appointments/stats');
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Error fetching appointment stats:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.current,
        limit: 20
      });

      if (statusFilter) params.append('status', statusFilter);
      if (merchantFilter) params.append('merchantId', merchantFilter);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await api(`/appointments?${params}`);
      const data = await response.json();

      if (data.success) {
        setAppointments(data.appointments || []);
        setPagination(data.pagination || { current: 1, pages: 1, total: 0 });
        setError(null);
      } else {
        setError(data.error || 'Failed to load appointments');
        setAppointments([]);
      }
    } catch (err) {
      console.error('Error fetching appointments:', err);
      setError(`Failed to load appointments: ${err.message}`);
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  };

  const openConfirmModal = (appointmentId, newStatus) => {
    const statusLabels = {
      CONFIRMED: 'Confirm',
      CANCELLED: 'Cancel',
      COMPLETED: 'Complete',
      NO_SHOW: 'mark as No Show'
    };

    setConfirmModal({
      isOpen: true,
      title: 'Update Status',
      message: `Are you sure you want to ${statusLabels[newStatus] || newStatus.toLowerCase()} this appointment?`,
      appointmentId,
      newStatus
    });
  };

  const closeConfirmModal = () => {
    setConfirmModal({
      isOpen: false,
      title: '',
      message: '',
      appointmentId: null,
      newStatus: null
    });
  };

  const handleStatusUpdate = async () => {
    const { appointmentId, newStatus } = confirmModal;
    closeConfirmModal();

    try {
      const response = await api(`/appointments/${appointmentId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });

      const data = await response.json();
      if (data.success) {
        fetchAppointments();
        fetchStats();
        showToast('success', 'Appointment status updated successfully');
      } else {
        showToast('error', data.error || 'Failed to update appointment status');
      }
    } catch (err) {
      console.error('Error updating appointment status:', err);
      showToast('error', 'Failed to update appointment status');
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'CONFIRMED': return 'am-status am-status-confirmed';
      case 'PENDING':
      case 'PENDING_PAYMENT': return 'am-status am-status-pending';
      case 'CANCELLED': return 'am-status am-status-cancelled';
      case 'COMPLETED': return 'am-status am-status-completed';
      case 'NO_SHOW': return 'am-status am-status-no-show';
      default: return 'am-status';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredAppointments = appointments.filter(appointment => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        appointment.customerName?.toLowerCase().includes(query) ||
        appointment.customerEmail?.toLowerCase().includes(query) ||
        appointment.serviceName?.toLowerCase().includes(query) ||
        appointment.merchant?.storeName?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  return (
    <div className="am-container">
      {/* Toast Notifications */}
      {toasts.length > 0 && (
        <div className="am-toast-container">
          {toasts.map(toast => (
            <Toast
              key={toast.id}
              type={toast.type}
              message={toast.message}
              onClose={() => removeToast(toast.id)}
            />
          ))}
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={handleStatusUpdate}
        onCancel={closeConfirmModal}
      />

      {/* Header */}
      <div className="am-header">
        <div className="am-header-content">
          <div className="am-header-text">
            <h1 className="am-header-title">
              <Calendar size={32} />
              Appointment Management
            </h1>
            <p className="am-header-subtitle">Monitor and manage appointments across all merchants</p>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      {!statsLoading && stats && (
        <div className="am-stats-grid">
          <div className="am-stat-card am-stat-total">
            <div className="am-stat-icon">
              <Calendar size={24} />
            </div>
            <div className="am-stat-content">
              <p className="am-stat-label">Total Appointments</p>
              <h3 className="am-stat-value">{stats.total?.toLocaleString()}</h3>
            </div>
          </div>

          <div className="am-stat-card am-stat-pending">
            <div className="am-stat-icon">
              <Clock size={24} />
            </div>
            <div className="am-stat-content">
              <p className="am-stat-label">Pending</p>
              <h3 className="am-stat-value">{stats.pending?.toLocaleString()}</h3>
            </div>
          </div>

          <div className="am-stat-card am-stat-confirmed">
            <div className="am-stat-icon">
              <CheckCircle size={24} />
            </div>
            <div className="am-stat-content">
              <p className="am-stat-label">Confirmed</p>
              <h3 className="am-stat-value">{stats.confirmed?.toLocaleString()}</h3>
            </div>
          </div>

          <div className="am-stat-card am-stat-completed">
            <div className="am-stat-icon">
              <TrendingUp size={24} />
            </div>
            <div className="am-stat-content">
              <p className="am-stat-label">Completed</p>
              <h3 className="am-stat-value">{stats.completed?.toLocaleString()}</h3>
            </div>
          </div>

          <div className="am-stat-card am-stat-revenue">
            <div className="am-stat-icon">
              <DollarSign size={24} />
            </div>
            <div className="am-stat-content">
              <p className="am-stat-label">Total Revenue</p>
              <h3 className="am-stat-value">{formatMoney(stats.totalRevenue || 0)}</h3>
            </div>
          </div>

          <div className="am-stat-card am-stat-cancelled">
            <div className="am-stat-icon">
              <XCircle size={24} />
            </div>
            <div className="am-stat-content">
              <p className="am-stat-label">Cancelled</p>
              <h3 className="am-stat-value">{stats.cancelled?.toLocaleString()}</h3>
            </div>
          </div>
        </div>
      )}

      {/* Filters Section */}
      <div className="am-filters">
        <div className="am-filters-header">
          <Filter size={20} />
          <h3>Filter Appointments</h3>
        </div>

        <div className="am-filters-grid">
          <div className="am-filter-group">
            <label className="am-filter-label">Search</label>
            <div className="am-search-wrapper">
              <Search size={18} />
              <input
                type="text"
                placeholder="Search by customer, service, or merchant..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="am-search-input"
              />
            </div>
          </div>

          <div className="am-filter-group">
            <label className="am-filter-label">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              className="am-filter-select"
            >
              <option value="">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="CONFIRMED">Confirmed</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="NO_SHOW">No Show</option>
              <option value="PENDING_PAYMENT">Pending Payment</option>
            </select>
          </div>

          <div className="am-filter-group">
            <label className="am-filter-label">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              className="am-filter-input"
            />
          </div>

          <div className="am-filter-group">
            <label className="am-filter-label">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              className="am-filter-input"
            />
          </div>

          {(statusFilter || startDate || endDate || searchQuery) && (
            <div className="am-filter-group">
              <label className="am-filter-label">&nbsp;</label>
              <button
                onClick={() => {
                  setStatusFilter('');
                  setStartDate('');
                  setEndDate('');
                  setSearchQuery('');
                  setPagination({ current: 1, pages: 1, total: 0 });
                }}
                className="am-clear-btn"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="am-error">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Loading State */}
      {loading && appointments.length === 0 ? (
        <div className="am-loading">
          <div className="am-spinner"></div>
          <p>Loading appointments...</p>
        </div>
      ) : filteredAppointments.length === 0 ? (
        <div className="am-empty">
          <Calendar size={64} />
          <h3>No appointments found</h3>
          <p>No appointments match your current filters</p>
        </div>
      ) : (
        <>
          {/* Appointments Grid */}
          <div className="am-grid">
            {filteredAppointments.map((appointment) => (
              <div key={appointment.id} className="am-card">
                <div className="am-card-header">
                  <div className="am-card-service">
                    <h3 className="am-service-name">{appointment.serviceName}</h3>
                    <span className={getStatusClass(appointment.status)}>
                      {appointment.status.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="am-card-price">
                    {formatMoney(appointment.servicePrice, appointment.merchant?.currency)}
                  </div>
                </div>

                <div className="am-card-body">
                  <div className="am-detail">
                    <Calendar size={16} />
                    <span>{formatDate(appointment.appointmentDate)} at {appointment.appointmentTime}</span>
                  </div>

                  <div className="am-detail">
                    <Clock size={16} />
                    <span>{appointment.serviceDuration} minutes</span>
                  </div>

                  <div className="am-detail">
                    <Users size={16} />
                    <span>{appointment.customerName}</span>
                  </div>

                  <div className="am-detail">
                    <span className="am-detail-label">Merchant:</span>
                    <span className="am-detail-merchant">{appointment.merchant?.storeName}</span>
                  </div>

                  {appointment.customerEmail && (
                    <div className="am-detail">
                      <span className="am-detail-label">Email:</span>
                      <span className="am-detail-truncate">{appointment.customerEmail}</span>
                    </div>
                  )}

                  {appointment.notes && (
                    <div className="am-detail am-detail-notes">
                      <span className="am-detail-label">Notes:</span>
                      <span className="am-detail-truncate">{appointment.notes}</span>
                    </div>
                  )}
                </div>

                <div className="am-card-actions">
                  {appointment.status === 'PENDING' && (
                    <>
                      <button
                        onClick={() => openConfirmModal(appointment.id, 'CONFIRMED')}
                        className="am-action-btn am-btn-confirm"
                      >
                        <CheckCircle size={16} />
                        Confirm
                      </button>
                      <button
                        onClick={() => openConfirmModal(appointment.id, 'CANCELLED')}
                        className="am-action-btn am-btn-cancel"
                      >
                        <XCircle size={16} />
                        Cancel
                      </button>
                    </>
                  )}

                  {appointment.status === 'CONFIRMED' && (
                    <>
                      <button
                        onClick={() => openConfirmModal(appointment.id, 'COMPLETED')}
                        className="am-action-btn am-btn-complete"
                      >
                        <CheckCircle size={16} />
                        Complete
                      </button>
                      <button
                        onClick={() => openConfirmModal(appointment.id, 'NO_SHOW')}
                        className="am-action-btn am-btn-no-show"
                      >
                        <AlertCircle size={16} />
                        No Show
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="am-pagination">
              <button
                onClick={() => setPagination(prev => ({ ...prev, current: prev.current - 1 }))}
                disabled={pagination.current === 1}
                className="am-pagination-btn"
              >
                Previous
              </button>
              <span className="am-pagination-info">
                Page {pagination.current} of {pagination.pages} ({pagination.total} total)
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, current: prev.current + 1 }))}
                disabled={pagination.current === pagination.pages}
                className="am-pagination-btn"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AppointmentManagement;
